import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Ticket, Plus, Power, Trash2, Users, ShieldCheck, CreditCard, Gift, RefreshCw, Search, KeyRound, UserX, Copy, Check } from "lucide-react";

function Stat({ icon, label, value, tone = "sage" }) {
  const bg = tone === "peach" ? "#F5ECE7" : "#E9ECE8";
  const fg = tone === "peach" ? "#D1A793" : "#6B7F62";
  return (
    <div className="card p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
           style={{ background: bg, color: fg }}>{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488]">{label}</div>
        <div className="font-serif text-3xl text-[#2D332D] leading-tight mt-1">{value}</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [stats, setStats] = useState(null);

  const [newCode, setNewCode] = useState("");
  const [newMaxUses, setNewMaxUses] = useState("");
  const [newDiscount, setNewDiscount] = useState("100");
  const [newNote, setNewNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [codesR, txR, statsR] = await Promise.all([
        api.get("/admin/promo-codes"),
        api.get("/admin/redemptions"),
        api.get("/admin/stats"),
      ]);
      setCodes(codesR.data.codes || []);
      setRedemptions(txR.data.transactions || []);
      setStats(statsR.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  if (user === null) return <div className="min-h-[40vh]" />;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const createCode = async (e) => {
    e.preventDefault();
    setError(""); setCreating(true);
    try {
      const payload = { code: newCode.trim().toUpperCase() };
      if (newMaxUses.trim()) payload.max_uses = parseInt(newMaxUses, 10);
      const pct = parseInt(newDiscount, 10);
      if (!Number.isFinite(pct) || pct < 1 || pct > 100) {
        setError("Discount must be between 1 and 100.");
        setCreating(false);
        return;
      }
      payload.discount_percent = pct;
      if (newNote.trim()) payload.note = newNote.trim();
      await api.post("/admin/promo-codes", payload);
      setNewCode(""); setNewMaxUses(""); setNewDiscount("100"); setNewNote("");
      await loadAll();
    } catch (e) { setError(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const toggleActive = async (code, active) => {
    try { await api.patch(`/admin/promo-codes/${code}`, { active: !active }); await loadAll(); }
    catch (e) { setError(formatApiError(e)); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete code ${code}? This can't be undone.`)) return;
    try { await api.delete(`/admin/promo-codes/${code}`); await loadAll(); }
    catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-3">Admin</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#2D332D]">Overview</h1>
          <p className="text-[#576056] mt-3">Manage promo codes and review transactions.</p>
        </div>
        <button onClick={loadAll} disabled={refreshing} className="btn-secondary text-sm" data-testid="admin-refresh">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12" data-testid="admin-stats">
          <Stat icon={<Users size={18} />} label="Users" value={stats.total_users} />
          <Stat icon={<ShieldCheck size={18} />} label="Premium" value={stats.premium_users} />
          <Stat icon={<CreditCard size={18} />} label="Paid" value={stats.paid_transactions} tone="peach" />
          <Stat icon={<Gift size={18} />} label="Promo redeemed" value={stats.promo_redemptions} tone="peach" />
          <Stat icon={<Ticket size={18} />} label="Active codes" value={stats.active_promo_codes} />
        </div>
      )}

      {error && <div className="mb-6 text-sm text-[#B05A5A] bg-[#B05A5A]/8 border border-[#B05A5A]/25 rounded-xl px-4 py-3">{error}</div>}

      {/* Users management */}
      <UsersSection />

      {/* Create new code */}
      <section className="card p-6 md:p-8 mb-10">
        <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-2">Create a code</div>
        <h2 className="font-serif text-2xl text-[#2D332D] mb-5">New promo code</h2>
        <form onSubmit={createCode} className="grid md:grid-cols-12 gap-3" data-testid="new-promo-form">
          <input
            data-testid="new-promo-code"
            type="text" required minLength={3}
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            placeholder="CODE"
            className="input md:col-span-3 uppercase tracking-wider"
          />
          <input
            data-testid="new-promo-max-uses"
            type="number" min="1"
            value={newMaxUses}
            onChange={(e) => setNewMaxUses(e.target.value)}
            placeholder="Max uses (blank = unlimited)"
            className="input md:col-span-3"
          />
          <div className="md:col-span-2 relative">
            <input
              data-testid="new-promo-discount"
              type="number" min="1" max="100" required
              value={newDiscount}
              onChange={(e) => setNewDiscount(e.target.value)}
              placeholder="Discount"
              className="input pr-8"
              title="Percentage off. 100 = full free unlock, 50 = half price"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A9488] text-sm pointer-events-none">%</span>
          </div>
          <input
            data-testid="new-promo-note"
            type="text"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Note (internal)"
            className="input md:col-span-2"
          />
          <button
            data-testid="new-promo-submit"
            disabled={creating}
            className="btn-primary md:col-span-2 justify-center"
          >
            <Plus size={16} /> {creating ? "…" : "Add"}
          </button>
        </form>
        <p className="text-xs text-[#8A9488] mt-3">
          <span className="font-medium text-[#576056]">Discount:</span> 100% = fully unlock premium instantly · anything less (e.g. 50%) reduces the £29 checkout price and the user still pays through Stripe.
        </p>
      </section>

      {/* Codes list */}
      <section className="mb-14">
        <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-4 flex items-center gap-2">
          <Ticket size={13} /> Promo codes ({codes.length})
        </div>
        <div className="card overflow-hidden" data-testid="promo-codes-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F1ED]">
                <tr className="text-left text-[#576056]">
                  <th className="px-5 py-3 font-medium">Code</th>
                  <th className="px-5 py-3 font-medium">Discount</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Uses</th>
                  <th className="px-5 py-3 font-medium">Note</th>
                  <th className="px-5 py-3 font-medium">Created</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAE6E1]">
                {codes.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-[#8A9488]">No codes yet.</td></tr>
                )}
                {codes.map((c) => (
                  <tr key={c.code} data-testid={`promo-row-${c.code}`}>
                    <td className="px-5 py-3 font-mono text-[#2D332D]">{c.code}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                        (c.discount_percent ?? 100) >= 100 ? "bg-[#F5ECE7] text-[#B47254]" : "bg-[#E9ECE8] text-[#4A6E55]"
                      }`}>
                        {(c.discount_percent ?? 100) >= 100 ? "Free (100%)" : `${c.discount_percent}% off`}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                        c.active ? "bg-[#E9ECE8] text-[#4A6E55]" : "bg-[#F3F1ED] text-[#8A9488]"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.active ? "bg-[#4A6E55]" : "bg-[#8A9488]"}`} />
                        {c.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[#2D332D]">
                      {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : " · unlimited"}
                    </td>
                    <td className="px-5 py-3 text-[#576056] max-w-[240px] truncate">{c.note || "—"}</td>
                    <td className="px-5 py-3 text-[#8A9488] text-xs">{c.created_at?.slice(0, 10)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`promo-toggle-${c.code}`}
                        onClick={() => toggleActive(c.code, c.active)}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#EAE6E1] hover:border-[#9EAD94] text-[#576056]"
                      >
                        <Power size={12} /> {c.active ? "Disable" : "Enable"}
                      </button>
                      <button
                        data-testid={`promo-delete-${c.code}`}
                        onClick={() => deleteCode(c.code)}
                        className="ml-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#EAE6E1] hover:border-[#B05A5A] hover:text-[#B05A5A] text-[#576056]"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Redemption / transaction history */}
      <section>
        <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-4 flex items-center gap-2">
          <CreditCard size={13} /> Transactions ({redemptions.length})
        </div>
        <div className="card overflow-hidden" data-testid="transactions-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F1ED]">
                <tr className="text-left text-[#576056]">
                  <th className="px-5 py-3 font-medium">When</th>
                  <th className="px-5 py-3 font-medium">User</th>
                  <th className="px-5 py-3 font-medium">Provider</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Ref</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAE6E1]">
                {redemptions.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8A9488]">No transactions yet.</td></tr>
                )}
                {redemptions.map((t) => (
                  <tr key={t.id}>
                    <td className="px-5 py-3 text-[#8A9488] text-xs whitespace-nowrap">
                      {t.created_at ? new Date(t.created_at).toLocaleString("en-GB") : "—"}
                    </td>
                    <td className="px-5 py-3 text-[#2D332D]">{t.user_email || "—"}</td>
                    <td className="px-5 py-3 text-[#576056] capitalize">
                      {t.provider}{t.promo_code ? ` · ${t.promo_code}` : ""}
                    </td>
                    <td className="px-5 py-3 text-[#2D332D]">
                      {t.amount == null ? "—" : `£${Number(t.amount).toFixed(2)}`}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                        t.payment_status === "paid" ? "bg-[#E9ECE8] text-[#4A6E55]" :
                        t.payment_status === "initiated" ? "bg-[#F5ECE7] text-[#C49C5E]" :
                        "bg-[#F3F1ED] text-[#8A9488]"
                      }`}>
                        {t.payment_status || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[#8A9488] text-xs font-mono">{(t.session_id || "").slice(0, 20)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}


function UsersSection() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // user obj
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState(null); // { link, email, expires_at }
  const [copied, setCopied] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setUsers(data.users || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  const togglePremium = async (u) => {
    setBusy(true); setErr("");
    try {
      const endpoint = u.is_premium ? "revoke-premium" : "grant-premium";
      await api.post(`/admin/users/${u.id}/${endpoint}`);
      await load(q);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const generateReset = async (u) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/users/${u.id}/password-reset`);
      setResetLink({ link: data.link, email: data.email, expires_at: data.expires_at });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`, { data: { confirm_email: confirmEmail } });
      setDeleteTarget(null);
      setConfirmEmail("");
      await load(q);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <section className="mb-14" data-testid="admin-users-section">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] flex items-center gap-2">
          <Users size={13} /> Users ({users.length})
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9488]" />
            <input
              data-testid="admin-users-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name"
              className="input pl-9 text-sm py-2 min-w-[240px]"
            />
          </div>
          <button data-testid="admin-users-search-submit" type="submit" className="btn-secondary text-sm px-4 py-2">Search</button>
        </form>
      </div>

      {err && (
        <div data-testid="admin-users-error" className="mb-4 text-sm text-[#B05A5A] bg-[#B05A5A]/8 border border-[#B05A5A]/25 rounded-xl px-4 py-3">{err}</div>
      )}

      <div className="card overflow-hidden" data-testid="admin-users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F1ED]">
              <tr className="text-left text-[#576056]">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium">Premium</th>
                <th className="px-5 py-3 font-medium">Plan</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAE6E1]">
              {loading && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8A9488]">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8A9488]">No users found.</td></tr>
              )}
              {!loading && users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-5 py-3 text-[#2D332D]">{u.name || "—"}{u.role === "admin" && <span className="ml-2 text-xs uppercase tracking-wider text-[#D1A793]">admin</span>}</td>
                  <td className="px-5 py-3 text-[#576056]">{u.email}</td>
                  <td className="px-5 py-3 text-[#8A9488]">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                      u.is_premium ? "bg-[#F5ECE7] text-[#B47254]" : "bg-[#F3F1ED] text-[#8A9488]"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.is_premium ? "bg-[#D1A793]" : "bg-[#8A9488]"}`} />
                      {u.is_premium ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-[#EAE6E1] min-w-[60px] max-w-[100px] overflow-hidden">
                        <div className="h-full bg-[#6B7F62]" style={{ width: `${u.plan_completion}%` }} />
                      </div>
                      <span className="text-xs text-[#576056] tabular-nums">{u.plan_completion}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        disabled={busy || u.role === "admin"}
                        onClick={() => togglePremium(u)}
                        data-testid={`admin-user-toggle-premium-${u.id}`}
                        title={u.is_premium ? "Revoke premium" : "Grant premium"}
                        className="p-2 rounded-lg text-[#6B7F62] hover:bg-[#E9ECE8] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ShieldCheck size={15} />
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => generateReset(u)}
                        data-testid={`admin-user-reset-${u.id}`}
                        title="Generate password reset link"
                        className="p-2 rounded-lg text-[#6B7F62] hover:bg-[#E9ECE8] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <KeyRound size={15} />
                      </button>
                      <button
                        type="button"
                        disabled={busy || u.role === "admin"}
                        onClick={() => { setDeleteTarget(u); setConfirmEmail(""); }}
                        data-testid={`admin-user-delete-${u.id}`}
                        title="Delete user"
                        className="p-2 rounded-lg text-[#B04A3D] hover:bg-[#F5ECE7] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <UserX size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-delete-modal">
          <div className="bg-[#FBF9F5] rounded-2xl max-w-md w-full p-6 md:p-8 border border-[#D1A793]/40">
            <div className="font-serif text-2xl text-[#2D332D] mb-2">Delete this user?</div>
            <p className="text-sm text-[#576056] mb-4 leading-relaxed">
              This will permanently delete <span className="text-[#2D332D] font-medium">{deleteTarget.email}</span> and every piece of their data — Plan, letters, checklist progress, chats, and payment records. This can't be undone.
            </p>
            <label className="block text-sm font-medium text-[#2D332D] mb-1.5">Type the user's email to confirm</label>
            <input
              data-testid="admin-delete-confirm-email"
              type="text"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={deleteTarget.email}
              className="input mb-4"
              autoComplete="off"
            />
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setDeleteTarget(null); setConfirmEmail(""); }}
                disabled={busy}
                data-testid="admin-delete-cancel"
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || confirmEmail.trim().toLowerCase() !== deleteTarget.email.toLowerCase()}
                onClick={confirmDelete}
                data-testid="admin-delete-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#B04A3D] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#8f3b31] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={15} /> {busy ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset link modal */}
      {resetLink && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-reset-modal">
          <div className="bg-[#FBF9F5] rounded-2xl max-w-lg w-full p-6 md:p-8 border border-[#9EAD94]/40">
            <div className="font-serif text-2xl text-[#2D332D] mb-2">Password reset link</div>
            <p className="text-sm text-[#576056] mb-4 leading-relaxed">
              Copy this link and send it to <span className="text-[#2D332D] font-medium">{resetLink.email}</span> yourself. It expires in 30 minutes and can only be used once.
              <br /><span className="text-xs text-[#8A9488] italic">(When email is wired up, this link will be emailed automatically.)</span>
            </p>
            <div className="flex items-stretch gap-2 mb-4">
              <input
                readOnly
                value={resetLink.link}
                data-testid="admin-reset-link"
                className="input text-xs font-mono flex-1"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={copyLink}
                data-testid="admin-reset-copy"
                className="btn-secondary text-sm px-4 whitespace-nowrap"
              >
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setResetLink(null); setCopied(false); }}
                data-testid="admin-reset-close"
                className="btn-secondary text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
